import type { ActionKind } from '@fafi/shared';

// Clip catalog + selection for the skinned-mesh renderer — the pure, tested core; animatedPlayers.ts
// keeps the three.js glue. Presentation-only (§13.5): every input arrives on ViewEntity, the sim is
// untouched.
//
// D107: the Soccer Game Pack gave 11 more ActionKind states a real clip, and `idle` stopped being the
// run clip frozen at timeScale 0. Shipping all of them on the blocking load would have put ~375 KB
// and 11 extra requests in front of first render, so clips are TIERED: tier 1 blocks, tiers 2–3 stream
// in behind it. Anything not yet loaded degrades down CLIP_FALLBACK until it hits a tier-1 clip, so a
// rig is never stranded without something to play.

export type PlayerClipKind =
  | 'idle' | 'idle_2' | 'idle_3' | 'run' | 'sprint' | 'kick' | 'slide' | 'punch'
  | 'tackle_stand' | 'trip' | 'fallen' | 'getup' | 'header' | 'trap' | 'block'
  | 'celebrate' | 'celebrate_2' | 'celebrate_3'
  | 'gk_dive' | 'gk_hold' | 'gk_smother'
  // D126: the keeper's directional vocabulary. Absent assets fall back to the un-mirrored clips below,
  // so these can ship ahead of the GLBs and light up the moment `retarget-anim --mirror` produces them.
  | 'gk_dive_l' | 'gk_dive_r' | 'gk_sidestep_l' | 'gk_sidestep_r';

export interface ClipInput {
  action: ActionKind;
  moveSpeed: number;
  /** D126 — ticks the action has run. One-shots are SLAVED to this instead of guessing with timeScale:
   *  a 3.2 s dive clip played at 1× over a 16-tick action only ever showed its first 17 % (the crouch). */
  actionTicks?: number;
  /** D126 — which way this action goes: −1 left, +1 right, 0 unknown. Left dives finally look left. */
  side?: -1 | 0 | 1;
  /** D126 — is this rig a keeper? Gates the lateral sidestep vocabulary to the goal. */
  isGk?: boolean;
  /** Entity id. Picks this player's idle variant — stable for the whole match. */
  variant?: number;
  /** Changes per goal (the running score). Only shifts the CELEBRATION, so idles stay put. */
  celebrationSeed?: number;
}

/** Celebration variants, assigned per entity id. Order is the assignment order — keep it stable. */
export const CELEBRATION_CLIPS = ['celebrate', 'celebrate_2', 'celebrate_3'] as const satisfies readonly PlayerClipKind[];

/**
 * Idle variants (D109). `idle` is the mandatory tier-1 one everything falls back to; the other two
 * stream in with tier 2.
 *
 * More clips alone does NOT fix a line of players breathing in unison — with 3 clips and 11 players
 * you still get three synchronised groups. The fix is `idleStartOffset` + `idleTimeScale` below.
 */
export const IDLE_CLIPS = ['idle', 'idle_2', 'idle_3'] as const satisfies readonly PlayerClipKind[];

/** Fractional part, for the golden-ratio hashes below. */
const frac = (v: number): number => v - Math.floor(v);
/** 1/φ — successive multiples land maximally far apart, so consecutive ids never clump. */
const GOLDEN = 0.618033988749895;
/** Detune range: ±4%. Enough that two players drift apart, too little to read as slow-motion. */
const TIME_SCALE_SPREAD = 0.04;

/**
 * Where in its loop this player starts idling. Deterministic (same on every client, stable for the
 * whole match) and golden-ratio spread, because teammates get CONSECUTIVE entity ids — the one case
 * a naive `seed % n` would bunch into adjacent slivers of the loop.
 */
export function idleStartOffset(seed: number, duration: number): number {
  return frac((seed + 1) * GOLDEN) * duration;
}

/**
 * Slightly detuned playback per player. The offset alone only helps until two players happen to line
 * up; different speeds mean they keep drifting apart instead of locking back into step.
 */
export function idleTimeScale(seed: number): number {
  return 1 + (frac((seed + 1) * 0.7548776662466927) - 0.5) * 2 * TIME_SCALE_SPREAD;
}

/**
 * Load order. Tier 1 is everything a kickoff can show in its first seconds and is awaited before the
 * pool resolves; tiers 2–3 arrive later and are best-effort — a failed deferred fetch leaves the
 * fallback in place rather than breaking the match.
 */
export const CLIP_TIERS = [
  ['idle', 'run', 'sprint', 'kick', 'slide', 'punch'],
  ['idle_2', 'idle_3', 'tackle_stand', 'trip', 'fallen', 'getup', 'header', 'trap', 'block', 'celebrate', 'celebrate_2', 'celebrate_3'],
  ['gk_dive', 'gk_hold', 'gk_smother'],
  // D126: the keeper's directional clips are DECLARED (so pickClip can ask for them and the fallback
  // chain is defined) but deliberately NOT listed in a tier yet — a tier entry is a promise that
  // `anims_<clip>.glb` ships, and the mirrored/sidestep takes still have to come out of
  // `retarget-anim --mirror`. Until then every request degrades to the un-mirrored clip, which is
  // exactly today's look. Move these into tier 3 in the same commit as the assets.
] as const satisfies readonly (readonly PlayerClipKind[])[];

export const ALL_CLIPS: readonly PlayerClipKind[] = CLIP_TIERS.flat();

/** What to play while a clip is still in flight. Tier-1 clips are mandatory, hence null. */
export const CLIP_FALLBACK: Readonly<Record<PlayerClipKind, PlayerClipKind | null>> = {
  idle: null, run: null, sprint: null, kick: null, slide: null, punch: null,
  idle_2: 'idle',
  idle_3: 'idle',
  tackle_stand: 'slide',
  trip: 'slide',
  fallen: 'slide',
  getup: 'idle',
  header: 'idle',
  trap: 'idle',
  block: 'idle',
  celebrate: 'idle',
  celebrate_2: 'celebrate', // prefer another celebration over standing still
  celebrate_3: 'celebrate',
  gk_dive: 'slide',
  gk_hold: 'idle',
  gk_smother: 'slide',
  // D126: a mirrored dive degrades to the un-mirrored one (today's look) before it degrades to a slide
  gk_dive_l: 'gk_dive',
  gk_dive_r: 'gk_dive',
  gk_sidestep_l: 'run',
  gk_sidestep_r: 'run',
};

/** Played once and clamped on the final frame; everything else loops. */
export const ONE_SHOT_CLIPS: ReadonlySet<PlayerClipKind> = new Set([
  'kick', 'slide', 'punch', 'tackle_stand', 'trip', 'getup', 'header', 'trap', 'gk_dive', 'gk_hold', 'gk_smother',
]);

/**
 * Clips that lay the body down on their own. D55's theatrical tumble rotates the whole rig 90°, which
 * is right when the fall is being mimed by the borrowed slide clip and doubles up — burying the
 * player — once a real prone clip is playing. The rig checks this against the clip it ACTUALLY
 * resolved, so the tumble stays while tier 2 is in flight and switches off when it lands.
 */
export const DOWN_CLIPS: ReadonlySet<PlayerClipKind> = new Set(['trip', 'fallen']);

/** Base MAX_SPEED is 8.0 and sprint = 8.0 × SPRINT_MUL(1.35) — only sprinting exceeds 8.5. */
export const SPRINT_ANIM_MIN_SPEED = 8.5;
/** Below this the player reads as standing (TURN_EXEMPT_SPEED is 0.5; anything slower is idle-ish). */
export const RUN_ANIM_MIN_SPEED = 0.6;

export function pickClip({ action, moveSpeed, variant, celebrationSeed, side, isGk }: ClipInput): PlayerClipKind {
  switch (action) {
    case 'CELEBRATE': {
      // Entity id spreads celebrants across the clips; the per-goal seed rerolls which one each of
      // them does. Math.abs guards a negative or absent id.
      const i = Math.abs(Math.trunc((variant ?? 0) + (celebrationSeed ?? 0))) % CELEBRATION_CLIPS.length;
      return CELEBRATION_CLIPS[i] as PlayerClipKind;
    }
    case 'KICK_WINDUP':
    case 'KICK_RELEASE':
      return 'kick';
    case 'TACKLE_SLIDE':
    case 'SLIDE_RECOVERY':
      return 'slide';
    case 'TACKLE_STAND':
      return 'tackle_stand';
    case 'PUNCH_WINDUP':
    case 'PUNCH_ACTIVE':
    case 'PUNCH_RECOVERY':
      return 'punch';
    case 'RAGDOLL': // the knockdown itself
    case 'DIVE_FALL':
      return 'trip';
    case 'PRONE': // settled on the floor
      return 'fallen';
    case 'GETUP':
      return 'getup';
    case 'HEADER_JUMP':
      return 'header';
    case 'TRAP':
      return 'trap';
    case 'BLOCK':
    case 'SHIELD': // both are a braced, planted stance — one clip covers them
      return 'block';
    case 'GK_DIVE':
      // D126: left dives look left. `side` comes from the lateral velocity at action entry, so it is
      // the sim's own dive direction rather than a guess made in the renderer.
      return side === -1 ? 'gk_dive_l' : side === 1 ? 'gk_dive_r' : 'gk_dive';
    case 'GK_HOLD':
      return 'gk_hold';
    case 'GK_SMOTHER':
      return 'gk_smother';
    default:
      break; // SHOVE has no clip yet — it rides locomotion
  }
  // D126: a keeper shuffling along his line is not jogging forward. The 6 m/s shuffle used to render as
  // the forward `run` clip pinned at 2.2× — a scurry. Sidestep instead, when the motion is lateral.
  if (isGk === true && side !== 0 && side !== undefined && moveSpeed >= RUN_ANIM_MIN_SPEED) {
    return side === -1 ? 'gk_sidestep_l' : 'gk_sidestep_r';
  }
  if (moveSpeed >= SPRINT_ANIM_MIN_SPEED) return 'sprint';
  if (moveSpeed >= RUN_ANIM_MIN_SPEED) return 'run';
  return IDLE_CLIPS[Math.abs(Math.trunc(variant ?? 0)) % IDLE_CLIPS.length] as PlayerClipKind;
}

/** D55: is this action a floor state? Drives the rig's theatrical lay-flat tumble. */
export function isDownAction(action: ActionKind): boolean {
  return action === 'RAGDOLL' || action === 'PRONE' || action === 'DIVE_FALL';
}

/**
 * The best clip a rig can actually play right now: the requested one if loaded, else the nearest
 * loaded substitute down the fallback chain. Terminates at a tier-1 clip, which is always present.
 */
export function resolveAvailableClip(clip: PlayerClipKind, isLoaded: (c: PlayerClipKind) => boolean): PlayerClipKind {
  let cursor: PlayerClipKind | null = clip;
  while (cursor && !isLoaded(cursor)) cursor = CLIP_FALLBACK[cursor];
  return cursor ?? 'run';
}
